import {
  AccountTradingPlanStatusEnum,
  PlanPhaseTypeEnum,
  TradingAccountStatusesEnum,
  TradingPlan,
} from '../../models/crm/types';
import { isDemoAccountReference, isUsableAccountStatus } from '../../util/account-helpers';
import { isCallStatusReady } from '../../util/error-handling/StatusByCallHelpers';
import { NullableNumber } from '../../util/types';
import { isPlanPurchaseCallbackInProgress } from '../../util/url-helpers';
import { getCurrentAccount, getCurrentDetailedAccount } from '../crm/helpers';
import { AccountEndReason } from './index';
import { CRMCall } from '../crm/types';
import { RootState } from '../index';

export const areTradingPlansReady = (state: RootState): boolean =>
  isCallStatusReady(CRMCall.searchTradingPlans, state.crm.statusByCall);

const getPlanPhase = (plans: TradingPlan[], tradingPlanId: NullableNumber): PlanPhaseTypeEnum|undefined =>
  plans.find(plan => plan.id === tradingPlanId)?.phase;

export const isFirstEntryFundedAccount = (state: RootState, tradingPlanId: NullableNumber): boolean => {
  const { tradingPlans } = state.crm;

  if (getPlanPhase(tradingPlans, tradingPlanId) !== PlanPhaseTypeEnum.entry) {
    return false;
  }

  return !state.crm.accountHistory.tradingPlanIdsHeld
    .some(planId => getPlanPhase(tradingPlans, planId) === PlanPhaseTypeEnum.entry);
};

export const getCreatedAccountStatus = (
  state: RootState,
  tradingPlanId: NullableNumber,
): TradingAccountStatusesEnum => (
  isFirstEntryFundedAccount(state, tradingPlanId)
    ? TradingAccountStatusesEnum.ForApproval
    : TradingAccountStatusesEnum.PendingCreation
);

export const isNonDemoAccountBeingCreated = (state: RootState): boolean => {
  const pendingReference = state.crm.pendingTradingAccountReference;

  return (!!pendingReference && !isDemoAccountReference(pendingReference))
    || isPlanPurchaseCallbackInProgress();
};

const hasNonDemoTradingAccount = (state: RootState): boolean =>
  (state.crm.detailedAccounts.tradingAccounts ?? [])
    .some(({ reference_id }) => !isDemoAccountReference(reference_id));

export const hasNonDemoAccountPendingReferral = (state: RootState): boolean =>
  isNonDemoAccountBeingCreated(state) || hasNonDemoTradingAccount(state);

export const isCurrentAccountAwaitingApproval = (state: RootState): boolean =>
  getCurrentDetailedAccount(state)?.account_status === TradingAccountStatusesEnum.ForApproval;

export const getCurrentAccountPhase = (state: RootState): PlanPhaseTypeEnum|undefined =>
  getPlanPhase(state.crm.tradingPlans, getCurrentDetailedAccount(state)?.trading_plan_id);

export const getCompletedPhase = (state: RootState): PlanPhaseTypeEnum|undefined => {
  const { finishedReason, finishedPhase } = state.accountFlow;

  // Recorded when the account finished, and the only source that survives the closeout reset the
  // controller performs in the same turn.
  if (finishedReason === AccountEndReason.Passed && finishedPhase) {
    return finishedPhase;
  }

  if (state.closeout.accountStatusData?.isTradingPlanCompleted) {
    return getCurrentAccountPhase(state);
  }

  const { lastClosedAccount } = state.crm.accountHistory;

  if (lastClosedAccount?.planStatus !== AccountTradingPlanStatusEnum.Successful) {
    return undefined;
  }

  return getPlanPhase(state.crm.tradingPlans, lastClosedAccount.tradingPlanId);
};

export const hasCurrentAccountBuyingPower = (state: RootState): boolean => {
  const currentAccount = getCurrentAccount(state);
  const balance = (state.trading?.balances ?? []).find(({ account }) => account === currentAccount);

  return (balance?.availBp ?? 0) > 0;
};

export const isEntryFundedAccountUsable = (state: RootState): boolean => {
  const account = getCurrentDetailedAccount(state);

  if (!account || getPlanPhase(state.crm.tradingPlans, account.trading_plan_id) !== PlanPhaseTypeEnum.entry) {
    return false;
  }

  return isUsableAccountStatus(account.account_status);
};

export const isCurrentAccountFailed = (state: RootState): boolean => {
  const { finishedAccountReference, finishedReason } = state.accountFlow;

  return finishedReason === AccountEndReason.Failed
    && !!finishedAccountReference
    && finishedAccountReference === getCurrentAccount(state);
};
